import { create } from 'zustand';
import { auth, googleProvider, db } from '../config/firebase';
import { 
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { createUserProfile } from '../services/database/users';
import { assignFreePlan } from '../services/database/plans';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: true,
  error: null,
  signInWithGoogle: async () => {
    try {
      set({ isLoading: true, error: null });
      
      const isWebContainer = window.location.hostname.includes('stackblitz.io') || 
                           window.location.hostname.includes('webcontainer.io');
      
      let result;
      if (isWebContainer) {
        await signInWithRedirect(auth, googleProvider);
        result = await getRedirectResult(auth);
      } else {
        try {
          result = await signInWithPopup(auth, googleProvider);
        } catch (popupError: any) {
          if (popupError.code === 'auth/popup-blocked') {
            await signInWithRedirect(auth, googleProvider);
            result = await getRedirectResult(auth);
          } else {
            throw popupError;
          }
        }
      }

      if (result?.user) {
        // Check if user profile exists
        const userRef = doc(db, 'users', result.user.uid);
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
          // Create new user profile
          await createUserProfile(
            result.user.uid,
            result.user.email || '',
            result.user.displayName || ''
          );
          // Assign free plan to new user
          await assignFreePlan(result.user.uid);
        }

        set({ user: result.user });
      }
    } catch (error: any) {
      console.error('Sign in error:', error);
      set({ 
        error: error.code === 'auth/unauthorized-domain'
          ? 'This domain is not authorized. Please ensure you are accessing the site through an authorized domain.'
          : 'Failed to sign in with Google. Please try again.'
      });
    } finally {
      set({ isLoading: false });
    }
  },
  signOut: async () => {
    try {
      set({ isLoading: true, error: null });
      await firebaseSignOut(auth);
      set({ user: null });
    } catch (error) {
      set({ error: 'Failed to sign out' });
      console.error('Sign out error:', error);
    } finally {
      set({ isLoading: false });
    }
  },
  clearError: () => set({ error: null })
}));

// Initialize auth state listener
onAuthStateChanged(auth, (user) => {
  useAuthStore.setState({ user, isLoading: false });
});

// Check for redirect result on load
getRedirectResult(auth)
  .then((result) => {
    if (result?.user) {
      useAuthStore.setState({ user: result.user });
    }
  })
  .catch((error) => {
    console.error('Redirect result error:', error);
    useAuthStore.setState({ 
      error: error.code === 'auth/unauthorized-domain'
        ? 'This domain is not authorized. Please ensure you are accessing the site through an authorized domain.'
        : 'Failed to complete sign in. Please try again.'
    });
  });